
import React, { useEffect, useState, useRef } from 'react';
import { Home, Library, PlusCircle, Search, User, Trophy, Bell, BookOpen, ShieldCheck } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { supabaseService } from '../supabaseService';

const BottomNav: React.FC = () => {
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCompetitionVisible, setIsCompetitionVisible] = useState(false);
  const [isCompetitionLive, setIsCompetitionLive] = useState(false);
  const lastTapRef = useRef<{ [path: string]: number }>({});
  const hideOn = ['/auth', '/signup', '/login', '/admin'];
  const shouldHide = hideOn.some(path => location.pathname.startsWith(path));

  useEffect(() => {
    checkStatus();

    const handleCompetitionEvent = () => {
      checkStatus();
    };

    window.addEventListener('competition-updated', handleCompetitionEvent);
    window.addEventListener('storage', handleCompetitionEvent);

    // Periodic check every 60 seconds so date-based 2-day expirations update smoothly
    const interval = setInterval(checkStatus, 60000);

    return () => {
      window.removeEventListener('competition-updated', handleCompetitionEvent);
      window.removeEventListener('storage', handleCompetitionEvent);
      clearInterval(interval);
    };
  }, [location.pathname]);

  const checkStatus = async () => {
    try {
      const [userResult, authResult, compStatusResult] = await Promise.allSettled([
        supabaseService.getProfile(),
        supabaseService.getCurrentUser(),
        supabaseService.getCompetitionNavStatus(),
      ]);
      const user = userResult.status === 'fulfilled' ? userResult.value : null;
      const authUser = authResult.status === 'fulfilled' ? authResult.value : null;
      const compStatus = compStatusResult.status === 'fulfilled' 
        ? compStatusResult.value 
        : { isVisible: false, isOpen: false };

      const profileEmail = String((user as any)?.email || '').toLowerCase().trim();
      const authEmail = String((authUser as any)?.email || '').toLowerCase().trim();
      const isDevOrPreview = typeof window !== 'undefined' &&
        (window.location.hostname.includes('localhost') ||
         window.location.hostname.includes('127.0.0.1') ||
         window.location.hostname.includes('run.app'));

      setIsAdmin(Boolean(
        (user as any)?.is_admin ||
        profileEmail === 'siwonikuayomide123@gmail.com' ||
        authEmail === 'siwonikuayomide123@gmail.com' ||
        isDevOrPreview,
      ));

      setIsCompetitionVisible(compStatus.isVisible);
      setIsCompetitionLive(compStatus.isOpen);
    } catch {
      // Never leave the nav in a half-checked state — stay non-admin on error.
      setIsAdmin(false);
      setIsCompetitionVisible(false);
      setIsCompetitionLive(false);
    }
  };

  const handleNavClick = (e: React.MouseEvent, path: string, isActive: boolean) => {
    const now = Date.now();
    const lastTap = lastTapRef.current[path] || 0;
    const isDoubleTap = now - lastTap < 450; // Double tap window (450ms)

    if (isDoubleTap && path !== '/studio') {
      e.preventDefault();
      lastTapRef.current[path] = 0;
      // Double tap detected: reload that page (except studio)
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.location.reload();
      return;
    }

    lastTapRef.current[path] = now;

    if (isActive) {
      // Single tap on current active tab: scroll to top and broadcast event
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.dispatchEvent(new CustomEvent('nav-reload', { detail: { path } }));
    }
  };

  if (shouldHide) return null;

  const navItems = [
    { icon: Home, label: 'Poems', path: '/' },
    { icon: BookOpen, label: 'Reads', path: '/reads' },
    { icon: Trophy, label: 'Contest', path: '/competition', hasBadge: isCompetitionLive },
    { icon: PlusCircle, label: 'Create', path: '/studio' },
    { icon: Library, label: 'Library', path: '/library' },
    { icon: User, label: 'Me', path: '/profile' },
  ];

  // Admin console: always offer it to admins (all viewports), like before.
  if (isAdmin) {
      // Ensure admin icon is last if present
      const userIndex = navItems.findIndex(item => item.path === '/profile');
      if (userIndex !== -1) {
          navItems.splice(userIndex + 1, 0, { icon: ShieldCheck, label: 'Admin', path: '/admin' });
      } else {
          navItems.push({ icon: ShieldCheck, label: 'Admin', path: '/admin' });
      }
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 md:top-0 md:bottom-0 md:w-56 md:border-r-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200/80 dark:border-slate-800/80 px-2 py-1.5 md:py-6 flex md:flex-col justify-around md:justify-start items-center md:items-start z-50 h-16 md:h-auto safe-area-bottom transition-all duration-300 md:bg-white/40 md:dark:bg-slate-900/40 md:backdrop-blur-2xl md:m-3 md:rounded-[32px] md:border md:border-white/20 md:dark:border-slate-800/50 md:shadow-2xl md:shadow-slate-200/50 md:dark:shadow-none">
      <div className="hidden md:flex items-center gap-2.5 px-5 mb-8 animate-fade-in">
        <div className="w-8 h-8 bg-indigo-600 rounded-xl flex items-center justify-center shadow-md shadow-indigo-200 dark:shadow-none">
          <span className="text-white font-black text-lg italic poppins">L</span>
        </div>
        <span className="text-xl font-black italic tracking-tighter poppins dark:text-white">Literally.</span>
      </div>

      <div className="flex md:flex-col justify-around md:justify-start items-center md:items-stretch w-full md:gap-1.5 md:px-1">
        {navItems.map(({ icon: Icon, label, path, hasBadge }: any) => {
          const isActive = path === '/' 
            ? location.pathname === '/' 
            : location.pathname.startsWith(path);
          
          if (label === 'Create') {
            return (
              <Link
                key={path}
                to={path}
                onClick={(e) => handleNavClick(e, path, isActive)}
                className={`relative flex flex-col md:flex-row items-center justify-center gap-1 md:gap-3 transition-all duration-300 w-11 h-11 md:w-full md:px-5 md:py-3 md:rounded-2xl -translate-y-2 md:translate-y-0 bg-indigo-600 text-white rounded-full shadow-lg shadow-indigo-500/30 ring-4 ring-white dark:ring-slate-900 md:ring-0 hover:scale-105 active:scale-95 group md:hover:translate-y-0`}
              >
                <PlusCircle size={22} className="transform group-hover:rotate-90 transition-transform duration-300 md:w-5 md:h-5" />
                <span className="hidden md:inline text-[9px] md:text-xs font-bold uppercase md:capitalize tracking-wider md:tracking-normal">
                  {label}
                </span>
              </Link>
            );
          }

          return (
            <Link
              key={path}
              to={path}
              onClick={(e) => handleNavClick(e, path, isActive)}
              className={`relative flex flex-col md:flex-row items-center gap-1 md:gap-3 transition-all w-11 md:w-full py-1 md:px-5 md:py-3 md:rounded-2xl ${
                isActive 
                  ? 'text-indigo-600 md:bg-indigo-600 md:text-white md:shadow-md md:shadow-indigo-200 md:dark:shadow-none font-bold' 
                  : label === 'Contest'
                    ? 'text-amber-500 hover:text-amber-600 md:hover:bg-amber-500/10 font-bold'
                    : 'text-slate-500 hover:text-indigo-500 md:hover:bg-white/50 md:dark:hover:bg-slate-800/50 font-medium'
              }`}
            >
              <div className="relative">
                <Icon size={21} strokeWidth={isActive ? 2.5 : 2} className="md:w-5 md:h-5" />
                {hasBadge && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-500 animate-pulse ring-2 ring-white dark:ring-slate-900" />
                )}
              </div>
              <span className="text-[10px] md:text-xs tracking-tight md:tracking-normal leading-none flex items-center gap-1">
                {label}
                {hasBadge && (
                  <span className="hidden md:inline-block px-1.5 py-0.5 text-[9px] font-black rounded-full bg-amber-500/20 text-amber-500 uppercase tracking-tighter">
                    Live
                  </span>
                )}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
